"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useAppSelector } from "@/store/hooks";

const Events = () => {
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.events;

  return (
    <section id="events" className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Images Left */}
          <div className="flex flex-col md:flex-row gap-6 items-center lg:items-start justify-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              className="relative w-full md:w-[400px] h-[400px] shadow-2xl z-20"
            >
              <Image
                src={
                  data?.image1 ||
                  "https://images.unsplash.com/photo-1544148103-0773bf10d330?q=80&w=3454&auto=format&fit=crop"
                }
                alt="Event atmosphere"
                fill
                className="object-cover"
                priority
              />
            </motion.div>

            {/* Second Image - Staggered Down */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              className="relative w-full md:w-[400px] h-[400px] shadow-2xl md:mt-8 z-10"
            >
              <Image
                src={
                  data?.image2 ||
                  "https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2074&auto=format&fit=crop"
                }
                alt="Gourmet catering"
                fill
                className="object-cover"
              />
            </motion.div>
          </div>

          {/* Content Right */}
          <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="flex flex-col items-center lg:items-start"
            >
              {/* Subtitle */}
              <p className="font-sacramento text-4xl text-[#c59d5f] mb-2 leading-relaxed">
                {data?.subtitle || "Host Your Event"}
              </p>

              {/* Main Heading */}
              <h2 className="font-cormorant text-[42px] md:text-[52px] text-[#212529] font-bold leading-[1.1] mb-8">
                {data?.title || (
                  <>
                    A Warm & Friendly
                    <br className="hidden md:block" /> Atmosphere
                  </>
                )}
              </h2>

              {/* Decorative Divider - Three Diamonds */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-12 h-[1px] bg-[#c59d5f]/40"></div>
                <div className="flex gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rotate-45 border border-[#c59d5f] bg-[#c59d5f]/20"
                    ></div>
                  ))}
                </div>
                <div className="w-12 h-[1px] bg-[#c59d5f]/40"></div>
              </div>

              {/* Description */}
              <p className="text-[#777777] text-[16px] md:text-[17px] leading-[1.8] mb-12 font-inter max-w-lg">
                {data?.description ||
                  "Allow us to make your next special event extra special. We cater for all sized functions, ideal for your intimate gathering, our team can curate a menu to suit your taste."}
              </p>

              {/* Action Link - Explore Events (All Caps & Bold) */}
              <a
                href={data?.button?.href || "/events"}
                className="relative inline-block font-playfair text-[16px] font-bold text-[#212529] border-b-[2px] border-[#212529] pb-1 hover:text-[#c59d5f] hover:border-[#c59d5f] transition-all duration-300"
              >
                {data?.button?.label || "Explore Events"}
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Events;
